import React from 'react';
import LoginRedirect from './LoginRedirect';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from './ui/dialog';
import { applyThemeMode, cn, useThemeMode } from '../design-system';
import './Auth/AuthSurface.css';

type LoginDialogProps = {
  analyticsContext?: Record<string, unknown>;
  forceFreshLogin?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAuthenticated?: () => void;
  returnTo: string;
  source?: string;
};

const LoginDialog = ({
  analyticsContext,
  forceFreshLogin,
  open,
  onAuthenticated,
  onOpenChange,
  returnTo,
  source = 'login_dialog',
}: LoginDialogProps): React.ReactElement => {
  const [themeMode] = useThemeMode();
  const themeAttribute = applyThemeMode(themeMode);
  const initialFocusRef = React.useRef<HTMLDivElement>(null);
  const handleOpenAutoFocus = React.useCallback((event: Event) => {
    event.preventDefault();
    requestAnimationFrame(() => {
      initialFocusRef.current?.focus({ preventScroll: true });
    });
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'auth-dialog-content',
          source === 'sales_auth_first' && 'auth-dialog-content--sales'
        )}
        onOpenAutoFocus={handleOpenAutoFocus}
      >
        <DialogTitle className="bsui-sr-only">
          {source === 'sales_auth_first' ? 'Create or sign in to your BotSpot workspace' : 'Sign In'}
        </DialogTitle>
        <DialogDescription className="bsui-sr-only">
          {source === 'sales_auth_first'
            ? 'Learn what BotSpot does, then continue with Google or email.'
            : 'Continue with Google or email.'}
        </DialogDescription>
        <div
          ref={initialFocusRef}
          className="auth-dialog-scope auth-page bsui-scope"
          data-bs-theme={themeAttribute}
          tabIndex={-1}
        >
          <LoginRedirect
            analyticsContext={analyticsContext}
            forceFreshLoginOverride={forceFreshLogin}
            onAuthenticated={() => {
              onAuthenticated?.();
              onOpenChange(false);
            }}
            returnToOverride={returnTo}
            sourceOverride={source}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LoginDialog;
